import { ref, nextTick, onMounted, onBeforeUnmount, type Ref } from 'vue'

import i18n from '@/i18n'

import { pickRandomSuggestions } from './promptSuggestions'

export interface PromptSuggestion {
  key: string
  label: string
}

const toSuggestions = (keys: string[]): PromptSuggestion[] => keys.map((key) => ({ key, label: i18n.t(key) }))

/**
 * Manages the "Try" prompt suggestions shown under the AI search input:
 * - keeps a set of random candidates (reshuffled via `shuffle`)
 * - shows only as many as fit on one line (`visibleCount`), recomputed on
 *   mount, container resize and reshuffle so long prompts never overflow.
 *
 * `rowRef` must point to the row element, `refreshRef` to the refresh button,
 * and each chip must carry the `data-suggestion-chip` attribute.
 */
export function usePromptSuggestions(
  rowRef: Ref<HTMLElement | null>,
  refreshRef: Ref<HTMLElement | null>,
  count = 3,
) {
  const candidates = ref<PromptSuggestion[]>(toSuggestions(pickRandomSuggestions(count)))
  const visibleCount = ref(count)

  // Show as many chips as fit on one line (min 1), reserving room for the refresh button.
  const updateVisibleCount = async () => {
    // Render all candidates first so their widths and positions can be measured.
    visibleCount.value = candidates.value.length
    await nextTick()

    const row = rowRef.value
    if (!row) return

    const chips = [...row.querySelectorAll<HTMLElement>('[data-suggestion-chip]')]
    const gap = parseFloat(getComputedStyle(row).columnGap || '0') || 0
    const refreshEl = refreshRef.value
    const refreshWidth = refreshEl?.offsetWidth ?? 0
    // The refresh button carries its own left margin (ml-1) on top of the flex gap.
    const refreshMargin = refreshEl ? parseFloat(getComputedStyle(refreshEl).marginLeft || '0') || 0 : 0
    // Right-most x a chip may occupy without pushing the refresh button off-line.
    const limit = row.clientWidth - refreshWidth - refreshMargin - gap

    let fits = 0
    for (const chip of chips) {
      // offsetLeft accounts for the "Try" label and preceding chips automatically.
      if (chip.offsetLeft + chip.offsetWidth <= limit) fits++
      else break
    }

    visibleCount.value = Math.max(1, fits)
  }

  const shuffle = () => {
    candidates.value = toSuggestions(pickRandomSuggestions(count, candidates.value.map((s) => s.key)))
    void updateVisibleCount()
  }

  let resizeObserver: ResizeObserver | undefined

  onMounted(() => {
    void updateVisibleCount()
    if (rowRef.value && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => void updateVisibleCount())
      resizeObserver.observe(rowRef.value)
    }
  })

  onBeforeUnmount(() => resizeObserver?.disconnect())

  return { candidates, visibleCount, shuffle }
}
